import { useCallback, useRef, useState } from 'react'
import incidentAssistantService from '../services/incidentAssistantService'
import {
  fallbackFirstSteps,
  getIncidentCategory,
} from '../utils/incidentReport'

/**
 * Primeros pasos para quien reporta un incidente: los pide a la IA y, si no
 * está disponible, usa los pasos básicos del tipo de incidente. La persona
 * nunca se queda sin guía.
 *
 * @returns {{
 *   guide: {summary: string, steps: Array<Object>, dont: string[], source: 'ai'|'fallback'}|null,
 *   loading: boolean,
 *   aiError: string|null,
 *   generate: (report: Object, options?: {force?: boolean}) => Promise<void>,
 *   retry: () => void,
 *   reset: () => void
 * }}
 */
export default function useIncidentFirstSteps() {
  const [guide, setGuide] = useState(null)
  const [loading, setLoading] = useState(false)
  const [aiError, setAiError] = useState(null)
  // Evita pedir dos veces la misma guía (p. ej. al volver al paso).
  const lastKey = useRef(null)
  const lastReport = useRef(null)

  /**
   * @param {Object} report - category (clave), description, severity,
   *   detectionSource, personalData, assets (nombres).
   * @param {Object} [options]
   * @param {boolean} [options.force=false] - Regenerar aunque no haya cambios.
   */
  const generate = useCallback(async (report, { force = false } = {}) => {
    const key = JSON.stringify(report)
    if (!force && key === lastKey.current) return
    lastKey.current = key
    lastReport.current = report

    setLoading(true)
    setAiError(null)
    const { success, data, error } = await incidentAssistantService.firstSteps({
      ...report,
      category: getIncidentCategory(report.category)?.label || report.category,
    })
    setLoading(false)

    if (success && data?.steps?.length) {
      setGuide({ ...data, source: 'ai' })
    } else {
      setAiError(error || 'La IA no devolvió pasos.')
      setGuide(fallbackFirstSteps(report.category))
    }
  }, [])

  /** Vuelve a pedir la guía del último reporte (p. ej. si la IA falló). */
  const retry = useCallback(() => {
    if (lastReport.current) generate(lastReport.current, { force: true })
  }, [generate])

  const reset = useCallback(() => {
    lastKey.current = null
    lastReport.current = null
    setGuide(null)
    setAiError(null)
  }, [])

  return { guide, loading, aiError, generate, retry, reset }
}
